import React, { useEffect, useState } from 'react';
import { AlertTriangle, ArrowRight, CheckCircle2, ExternalLink, HardDriveDownload } from 'lucide-react';
import { hardwareApi, type DeviceOrderRow, type DeviceUnitRow, type PendingReplacementRow } from '../../services/hardwareApi';
import { useDataVersion } from '../../services/dataChangeBus';
import { unitLabel } from './stageShell';
import { useReplaceWizard } from './replaceWizardHost';
import { installUrlFor, osInstalledForReplacement, replacementIdFor } from './replaceFlow';

/**
 * A REPLACEMENT THAT WAS STARTED AND NOT FINISHED — said at the top of the
 * order, one banner each, with the only way back into it.
 *
 * A bench swap takes the outgoing device OFF the order, so without this a
 * wizard closed half-way could never be reopened: nothing on the order pointed
 * at the device any more. `order.pendingReplacements` is that pointer, and
 * Continue opens the wizard in resume mode on it.
 *
 * THREE STATES, from live data — the replacement is found the same way the
 * wizard's resume finds it (`replacementIdFor`):
 *   no replacement on the order yet        → ruby, "not finished", Continue
 *   POS replacement, OS not installed yet  → amber, "waiting for OS installation",
 *                                            Install OS (Device management, new tab)
 *   installed (or not a POS), not attached → emerald, "installed — attach it"
 * The install happens in ANOTHER tab, so this re-reads when the order changes,
 * when this tab gets focus back, and every 15s while anything waits on an
 * install. It goes away when the order refresh no longer lists the entry.
 */

const INSTALL_POLL_MS = 15_000;

interface Found { old?: DeviceUnitRow | null; rep?: DeviceUnitRow | null }
type Phase = 'unpicked' | 'install' | 'attach';

export function phaseFor(p: PendingReplacementRow, orderId: string, f: Found | undefined): Phase {
  const rep = f?.rep;
  if (!rep) return 'unpicked';
  const placed = rep.orderId === orderId || (!!p.postDispatch && f?.old?.replacedByUnitId === rep.unitId);
  if (!placed) return 'unpicked';
  if (p.lane === 'pos' && !osInstalledForReplacement(rep, p.startedAt || 0)) return 'install';
  return 'attach';
}

const PendingReplacementBanners: React.FC<{
  order: DeviceOrderRow;
  units: DeviceUnitRow[];
  onChanged?: () => void;
}> = ({ order, units, onChanged }) => {
  const entries: PendingReplacementRow[] = Object.values(order.pendingReplacements || {});
  const dataVersion = useDataVersion();
  /** Per outgoing unit: its live row and the replacement it names, if any. */
  const [found, setFound] = useState<Record<string, Found>>({});
  /** Bumped by focus / visibility / the install poll — "read it all again". */
  const [tick, setTick] = useState(0);
  /*
    The wizard lives in the page's ReplaceWizardHost, never in this banner: the
    banner's own render depends on the order, and finishing a replacement
    changes exactly that.
  */
  const wizard = useReplaceWizard();

  const entryKey = entries.map((p) => `${p.unitId}:${p.startedAt || 0}`).join(',');
  useEffect(() => {
    if (entries.length === 0) return undefined;
    let alive = true;
    (async () => {
      const rows = await Promise.all(entries.map(async (p): Promise<[string, Found]> => {
        const old = (await hardwareApi.getUnit(p.unitId).catch(() => null))?.unit
          || units.find((u) => u.unitId === p.unitId) || null;
        const timeline = p.postDispatch
          ? []
          : (await hardwareApi.getUnitTimeline(p.unitId, { limit: 50 }).catch(() => null))?.items || [];
        const repId = replacementIdFor(p, old, timeline);
        const rep = repId ? (await hardwareApi.getUnit(repId).catch(() => null))?.unit || null : null;
        return [p.unitId, { old, rep }];
      }));
      if (alive) setFound(Object.fromEntries(rows));
    })();
    return () => { alive = false; };
  }, [entryKey, order.orderId, dataVersion, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const phases = entries.map((p) => phaseFor(p, order.orderId, found[p.unitId]));
  const waitingOnInstall = phases.includes('install');

  // The install happens in another tab: re-read when this one comes back.
  useEffect(() => {
    if (entries.length === 0) return undefined;
    const bump = () => setTick((t) => t + 1);
    const onVisible = () => { if (document.visibilityState === 'visible') bump(); };
    window.addEventListener('focus', bump);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', bump);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [entries.length]);
  useEffect(() => {
    if (!waitingOnInstall) return undefined;
    const every = window.setInterval(() => setTick((t) => t + 1), INSTALL_POLL_MS);
    return () => window.clearInterval(every);
  }, [waitingOnInstall]);

  const unitFor = (id: string) => units.find((u) => u.unitId === id) || found[id]?.old || undefined;
  const openWizard = (p: PendingReplacementRow) => {
    // A snapshot, taken once: the wizard only needs the id and re-reads the rest.
    const u = unitFor(p.unitId);
    wizard.open({
      unit: u ? { ...u } : { unitId: p.unitId },
      resume: p,
      orderId: order.orderId,
      onChanged,
    });
  };

  return (
    <>
      {entries.length > 0 && (
        <div className="mb-4 space-y-2">
          {entries.map((p, i) => {
            const u = unitFor(p.unitId);
            const name = u ? unitLabel(u) : p.unitId;
            const rep = found[p.unitId]?.rep;
            const repName = rep ? unitLabel(rep) : '';
            const phase = phases[i];
            const meta = (
              <p className="text-[11.5px] opacity-80">
                {p.postDispatch ? 'Field replacement' : 'Bench swap'} · “{p.reason}”
                {p.startedAt ? ` · started ${new Date(p.startedAt).toLocaleString()}` : ''}
                {p.actorEmail ? ` by ${p.actorEmail}` : ''}
              </p>
            );

            if (phase === 'install' && rep) {
              return (
                <div key={p.unitId} role="status"
                  className="flex flex-wrap items-center gap-3 rounded-xl border-2 border-amber-400 bg-amber-50 px-4 py-3 text-amber-900">
                  <HardDriveDownload size={18} className="shrink-0 text-amber-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-bold">{repName} is waiting for OS installation — replacing {name}</p>
                    {meta}
                  </div>
                  <button type="button" onClick={() => openWizard(p)}
                    className="min-h-[44px] rounded-xl border border-amber-300 bg-white px-4 py-2.5 text-sm font-semibold text-amber-900 hover:bg-amber-100">
                    Continue
                  </button>
                  <button type="button" onClick={() => window.open(installUrlFor(rep.unitId), '_blank')}
                    className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-700">
                    Install OS <ExternalLink size={14} />
                  </button>
                </div>
              );
            }

            if (phase === 'attach' && rep) {
              return (
                <div key={p.unitId} role="status"
                  className="flex flex-wrap items-center gap-3 rounded-xl border-2 border-emerald-500 bg-emerald-50 px-4 py-3 text-emerald-900">
                  <CheckCircle2 size={18} className="shrink-0 text-emerald-600" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-bold">
                      {p.lane === 'pos' ? `${repName} is installed — attach it to finish replacing ${name}` : `${repName} is on the order — attach it to finish replacing ${name}`}
                    </p>
                    {meta}
                  </div>
                  <button type="button" onClick={() => openWizard(p)}
                    className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700">
                    Attach it <ArrowRight size={14} />
                  </button>
                </div>
              );
            }

            return (
              <div key={p.unitId} role="status"
                className="flex flex-wrap items-center gap-3 rounded-xl border-2 border-[#DC143C] bg-[#FFF1F3] px-4 py-3 text-[#9F1239]">
                <AlertTriangle size={18} className="shrink-0 text-[#DC143C]" />
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-bold">Replacement of {name} not finished</p>
                  {meta}
                </div>
                <button type="button" onClick={() => openWizard(p)}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-[#DC143C] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#9F1239]">
                  Continue <ArrowRight size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
      {wizard.element}
    </>
  );
};

export default PendingReplacementBanners;
